// Apolo IDE — TasksPanel (sprint tasking-cursor-ide, P4+ Cursor-IDE port).
//
// The right-side "Tasks · Subagents · Eval" panel, pixel-aligned to
// apolo-chat-pos-s5.html. The Tasks tab renders the oracle-gated DAG from
// useAgentStore.code.tasks as a linear pipeline (knob + rail + line) in
// topological order, with depends_on + per-task oracle badges + a human
// approval gate. Subagents + Eval render REAL data when present and an
// honest empty-state otherwise (no fabricated metrics — honest-green).
//
// Pure presentation: reads the store, dispatches CustomEvents the workspace
// can wire (apolo:approveDag / apolo:reorderTasks / apolo:runOneTask). No
// fetch / no IPC here.

import { useMemo, useState } from 'react';
import { GitBranch, Layers, LineChart, ShieldCheck, Search, Terminal, Globe, FileText } from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { buildGraph, topoOrder, TASK_STATUS } from '../../../lib/tasks/taskGraph.js';
import MergeQueueLane from './MergeQueueLane.jsx';
import SpecsPanel from './SpecsPanel.jsx';
import { useMergeQueue } from '../../../hooks/useMergeQueue.js';
import useActiveDispatches from '../../../hooks/useActiveDispatches.js';
import useSubagentRuns from '../../../hooks/useSubagentRuns.js';
import { collectSubagents, panelSubagents, fmtTokens } from '../agents/agentFormat.js';

const TABS = [
  { id: 'tasks', label: 'Tasks', Icon: GitBranch },
  { id: 'specs', label: 'Specs', Icon: FileText },
  { id: 'subagents', label: 'Subagents', Icon: Layers },
  { id: 'eval', label: 'Eval', Icon: LineChart },
];

function statusOf(slice, id) {
  return slice.statusByPlanId?.[id]
    ?? slice.tasks.find((t) => t.id === id)?.status
    ?? TASK_STATUS.PENDING;
}

// Topological order, cycle-safe: falls back to insertion order on a bad DAG
// so the panel never throws.
function orderedIds(slice) {
  try {
    return topoOrder(buildGraph(slice.tasks));
  } catch {
    return slice.tasks.map((t) => t.id);
  }
}

function Knob({ state }) {
  if (state === TASK_STATUS.GREEN || state === TASK_STATUS.FAILED) {
    return (
      <span className="rx-tp-knob">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
      </span>
    );
  }
  if (state === TASK_STATUS.IN_PROGRESS) {
    return <span className="rx-tp-knob"><span className="rx-tp-spin" aria-hidden="true" /></span>;
  }
  return <span className="rx-tp-knob" aria-hidden="true" />;
}

function oracleLabel(state) {
  if (state === TASK_STATUS.GREEN) return { text: 'oracle: honest-green', pend: false };
  if (state === TASK_STATUS.IN_PROGRESS) return { text: 'oracle: verifying…', pend: true };
  if (state === TASK_STATUS.FAILED) return { text: 'oracle: failed', pend: true };
  return { text: 'pending', pend: true };
}

function TasksTab({ slice, mergeQueue, sessionId }) {
  const ids = useMemo(() => orderedIds(slice), [slice]);
  if (!slice.tasks.length) {
    return (
      <>
        <p className="rx-tp-empty" data-testid="tasks-empty">
          O grafo de tasks aparece aqui quando um plano gera tarefas
          (Requirements → Design → Tasks). Peça um plano no composer.
        </p>
        <MergeQueueLane view={mergeQueue} sessionId={sessionId} />
      </>
    );
  }
  const fire = (name, detail) => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }));
  };
  return (
    <>
      {/* (T6) Mission Control plan-doc header — Cursor's feature-prd.md vibe:
          plan title + body + "N Tasks", sitting above the DAG. Only when a
          planDoc rode in with the tasks (plan-propose / spec flow). */}
      {slice.planDoc?.title && (
        <div className="rx-tp-mc" data-testid="plan-doc">
          <div className="rx-tp-mc-title">{slice.planDoc.title}</div>
          {slice.planDoc.summary ? <div className="rx-tp-mc-sum">{slice.planDoc.summary}</div> : null}
          <div className="rx-tp-mc-count">
            {slice.tasks.length} {slice.tasks.length === 1 ? 'Task' : 'Tasks'}
          </div>
        </div>
      )}
      <div className="rx-tp-sec">Task graph · DAG · oracle-gated</div>
      <div className="rx-tp-dag" data-testid="dag">
        {ids.map((id, i) => {
          const t = slice.tasks.find((x) => x.id === id);
          if (!t) return null;
          const state = statusOf(slice, id);
          const oracle = oracleLabel(state);
          const last = i === ids.length - 1;
          return (
            <div key={id} className="rx-tp-node" data-state={state} data-testid={`node-${id}`}>
              <div className="rx-tp-rail">
                <Knob state={state} />
                {!last && <div className="rx-tp-vline" />}
              </div>
              <div className="rx-tp-ncard">
                <div className="rx-tp-ntitle">
                  <span className="rx-tp-id">{id}</span>
                  {t.name}
                </div>
                {t.acceptance && <div className="rx-tp-ndesc">{t.acceptance}</div>}
                {t.deps?.length > 0 && (
                  <div className="rx-tp-ndep">
                    <span className="rx-tp-b">depends_on:</span> {t.deps.join(', ')}
                  </div>
                )}
                <div className="rx-tp-oracle" data-pend={oracle.pend ? 'true' : 'false'}>
                  {!oracle.pend && (
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                  )}
                  {oracle.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {slice.approvedAt ? (
        <div className="rx-tp-gate" data-testid="dag-approved">
          <div className="rx-tp-gh rx-tp-gh--ok">
            <ShieldCheck size={13} aria-hidden="true" />
            Plano aprovado — executando
          </div>
        </div>
      ) : (
        <div className="rx-tp-gate">
          <div className="rx-tp-gh">
            <ShieldCheck size={13} aria-hidden="true" />
            Aprovação humana — antes da execução
          </div>
          <div className="rx-tp-actions">
            <button
              type="button"
              className="rx-tp-btn rx-tp-btn--primary"
              data-testid="approve-dag"
              onClick={() => fire('apolo:approveDag', { sessionId })}
            >
              Aprovar DAG
            </button>
          </div>
        </div>
      )}

      <MergeQueueLane view={mergeQueue} sessionId={sessionId} />
    </>
  );
}

const SUB_ICON = { explore: Search, bash: Terminal, browser: Globe };

function SubagentsTab({ subagents }) {
  const list = Array.isArray(subagents) ? subagents : [];
  if (!list.length) {
    return (
      <>
        <div className="rx-tp-sec">Subagents · context isolation</div>
        <p className="rx-tp-empty" data-testid="subagents-empty">
          Nenhum subagente ativo. Eles aparecem aqui quando uma task spawna
          Explore / Bash / Browser (só o resumo volta — contexto isolado).
        </p>
      </>
    );
  }
  return (
    <>
      <div className="rx-tp-sec">Subagents · context isolation</div>
      {list.map((s, i) => {
        const kind = String(s.kind || s.role || 'explore').toLowerCase();
        const Icon = SUB_ICON[kind] || Search;
        const running = String(s.status || '').toLowerCase() === 'running';
        return (
          <div key={s.id || i} className="rx-tp-sub" data-kind={SUB_ICON[kind] ? kind : 'explore'}>
            <span className="rx-tp-sic"><Icon size={13} aria-hidden="true" /></span>
            <div className="rx-tp-sm">
              <b>{s.name || s.role || 'Subagent'}</b>
              <span>{s.summary || s.task || '—'}</span>
            </div>
            {/* (T1) per-agent model badge + token chip — Cursor's "each subagent
                using the best model". Sibling of .rx-tp-sm so the .rx-tp-sm span
                rule doesn't override the badge color. Hidden when no model. */}
            {(s.model || s.tokens > 0) && (
              <span className="rx-tp-sbadge">
                {s.model ? (
                  <span className="rx-badge rx-badge--ai" title={`Modelo: ${s.model}`}>{s.model}</span>
                ) : null}
                {s.tokens > 0 ? <span className="rx-tp-tok">{fmtTokens(s.tokens)}</span> : null}
              </span>
            )}
            <span className="rx-tp-st" data-st={running ? 'run' : 'bg'}>
              {running ? 'running' : 'background'}
            </span>
          </div>
        );
      })}
    </>
  );
}

function EvalTab({ evalData }) {
  if (!evalData || (evalData.passRate == null && !Array.isArray(evalData.bars))) {
    return (
      <>
        <div className="rx-tp-sec">Harness eval · keep rate + A/B</div>
        <p className="rx-tp-empty" data-testid="eval-empty">
          Sem dados de avaliação ainda. As métricas (oracle pass-rate, keep
          rate, A/B tiered vs flat) aparecem após runs verificados pelo oracle.
        </p>
      </>
    );
  }
  return (
    <>
      <div className="rx-tp-sec">Harness eval · keep rate + A/B</div>
      <div className="rx-tp-dash">
        {/* E5/D — cada stat só aparece com dado real (oracle sem produtor de
            benchmark fica oculto; keep rate vem do keepRateStore vivo). */}
        {evalData.passRate != null && (
          <div className="rx-tp-stat">
            <div className="rx-tp-v">{evalData.passRate}<small>%</small></div>
            <div className="rx-tp-k">oracle pass-rate</div>
          </div>
        )}
        {evalData.keepRate != null && (
          <div className="rx-tp-stat">
            <div className="rx-tp-v">{evalData.keepRate}<small>%</small></div>
            <div className="rx-tp-k">keep rate · 7d</div>
          </div>
        )}
      </div>
      {Array.isArray(evalData.bars) && (
        <div className="rx-tp-bars">
          {evalData.bars.map((b) => (
            <div key={b.label} className="rx-tp-bar">
              <span className="rx-tp-lab">{b.label}</span>
              <span className="rx-tp-track">
                <span className="rx-tp-fill" data-win={b.win ? 'true' : 'false'} style={{ width: `${b.pct}%` }} />
              </span>
              <span>{b.pct}%</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function TasksPanel({ sessionId, subagents = [], evalData = null }) {
  const slice = useAgentStore((s) => s.code.tasks);
  const mergeQueue = useAgentStore((s) => s.code.mergeQueue);
  const [active, setActive] = useState('tasks');
  // P4-T02 — poll the merge-queue read-model into the store while the panel is up.
  useMergeQueue(sessionId);
  // pos-s5 — LIVE subagents: in-turn S5 runs (explore/bash/browser, store-based,
  // no poll) + dispatched Team/Hermes agents (polled ONLY while this tab is open).
  // An explicit `subagents` prop still wins (tests / future callers).
  const s5runs = useSubagentRuns(sessionId);
  const { workflows, recent } = useActiveDispatches({
    sessionId,
    enabled: active === 'subagents',
    withDetail: true,
  });
  const liveSubagents = useMemo(
    () => panelSubagents(s5runs, collectSubagents(workflows, recent)),
    [s5runs, workflows, recent],
  );
  const subagentsToShow = subagents.length ? subagents : liveSubagents;

  return (
    <section className="rx-tp" aria-label="Painel de tasks, subagents e avaliação" data-testid="tasks-panel">
      <div className="rx-tp-tabs" role="tablist" aria-label="Painéis do agente">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active === id}
            className="rx-tp-tab"
            data-testid={`tp-tab-${id}`}
            onClick={() => setActive(id)}
          >
            <Icon size={13} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>
      <div className="rx-tp-body">
        {active === 'tasks' && <TasksTab slice={slice} mergeQueue={mergeQueue} sessionId={sessionId} />}
        {active === 'specs' && <SpecsPanel sessionId={sessionId} />}
        {active === 'subagents' && <SubagentsTab subagents={subagentsToShow} />}
        {active === 'eval' && <EvalTab evalData={evalData} />}
      </div>
    </section>
  );
}
